import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Mail, Lock, User, Building, Factory, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { ThemeToggle } from '@/components/ThemeToggle';

const LOGO_URL = "/brand/kanriva-logo.png";
const LOGO_URL_LIGHT = "/brand/kanriva-logo.png";
const LOGIN_BG = "/brand/login-bg.jpg";
const LOGIN_BG_ALT = "/brand/login-bg-alt.jpg";

// Safe error message extractor - prevents non-cloneable objects from reaching toast
const getErrorMessage = (error, fallback = 'Something went wrong') => {
  try {
    if (typeof error === 'string') return error;
    if (error?.message && typeof error.message === 'string') {
      if (error.message.includes('postMessage') || error.message.includes('cloned')) {
        return fallback;
      }
      return error.message;
    }
    return fallback;
  } catch {
    return fallback;
  }
};

const industries = [
  { value: 'logistics', label: 'Logistics & Supply Chain' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'retail', label: 'Retail' },
  { value: 'other', label: 'Other' },
];

export const LoginPage = () => {
  const [email, setEmail] = useState(localStorage.getItem('saved_email') || '');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(localStorage.getItem('remember_me') === 'true');
  const [loading, setLoading] = useState(false);
  const [guestLoading, setGuestLoading] = useState(false);
  const { login, guestLogin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password, rememberMe);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#050a14]" data-testid="login-page">
      {/* Full-bleed brand backdrop */}
      <img
        src={LOGIN_BG}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover object-center scale-105 animate-[kenburns_36s_ease-in-out_infinite_alternate]"
      />
      <div className="absolute inset-0 bg-[#050a14]/72" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050a14] via-[#050a14]/45 to-[#050a14]/85" />
      <div className="absolute inset-0 bg-[radial-gradient(120%_90%_at_50%_-10%,rgba(37,99,235,0.28),transparent_60%)]" />

      <div className="relative z-10 flex min-h-screen flex-col">
        {/* Top bar */}
        <header className="flex items-center justify-between px-6 py-6 sm:px-10">
          <Link
            to="/"
            className="group flex items-center gap-2 text-sm font-medium text-white/55 transition-colors hover:text-white"
            data-testid="login-back-home"
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            Back to home
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <p className="hidden text-[10px] uppercase tracking-[0.22em] text-white/35 sm:block">
              powered by Kaizen Kanri
            </p>
          </div>
        </header>

        {/* Centered sign-in */}
        <main className="flex flex-1 items-center justify-center px-4 pb-10 sm:px-6">
          <div className="w-full max-w-[420px] animate-[fadeIn_0.7s_ease-out_both]">
            <div className="mb-8 text-center">
              <span className="inline-flex items-start justify-center">
                <img src={LOGO_URL} alt="Kanriva™" className="h-10 sm:h-11" />
              </span>
              <p className="mt-5 text-sm text-white/50">Sign in to your operation.</p>
            </div>

            <div
              className="rounded-2xl border border-white/12 bg-[#070d1a]/60 p-6 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)] backdrop-blur-2xl sm:p-7"
              data-testid="login-card"
            >
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs font-medium uppercase tracking-wider text-white/45">
                    Username or email
                  </Label>
                  <div className="relative">
                    <Mail className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
                    <Input
                      id="email"
                      type="text"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="h-12 rounded-xl border-white/12 bg-white/[0.06] pl-11 text-white placeholder:text-white/25 transition-colors focus:border-cyan-400/60 focus:bg-white/[0.09] focus-visible:ring-cyan-400/30"
                      required
                      data-testid="login-email"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs font-medium uppercase tracking-wider text-white/45">
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="pointer-events-none absolute left-3.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-white/35" />
                    <PasswordInput
                      id="password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-12 rounded-xl border-white/12 bg-white/[0.06] pl-11 text-white placeholder:text-white/25 transition-colors focus:border-cyan-400/60 focus:bg-white/[0.09] focus-visible:ring-cyan-400/30"
                      required
                      testId="login-password"
                    />
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <input
                      id="remember-me"
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 cursor-pointer rounded border-white/25 bg-white/10 text-cyan-500 focus:ring-cyan-500/40"
                      data-testid="remember-me-checkbox"
                    />
                    <Label htmlFor="remember-me" className="cursor-pointer select-none text-sm text-white/50">
                      Remember me
                    </Label>
                  </div>
                  <a
                    href={`https://wa.me/31613225788?text=${encodeURIComponent("Hi Daniel, I can't sign in to Kanriva™. My email is: ")}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-white/40 underline decoration-white/20 underline-offset-2 transition-colors hover:text-white/80"
                    data-testid="login-help-whatsapp"
                  >
                    Need help?
                  </a>
                </div>

                <Button
                  type="submit"
                  className="h-12 w-full rounded-xl bg-cyan-500 text-[15px] font-semibold text-[#04141a] transition-all hover:bg-cyan-400 hover:shadow-[0_0_36px_-8px_rgba(34,211,238,0.65)]"
                  disabled={loading}
                  data-testid="login-submit"
                >
                  {loading ? 'Signing in…' : 'Sign in'}
                </Button>
              </form>

              <div className="mt-6 border-t border-white/10 pt-5 text-center">
                <p className="text-xs text-white/40">
                  New to Kanriva™?{' '}
                  <Link to="/signup" className="font-medium text-cyan-300 hover:text-cyan-200" data-testid="login-signup-link">
                    Start a free 14-day trial
                  </Link>
                </p>
                <p className="mt-2 text-[11px] text-white/40">
                  No account yet? Contact your administrator or{' '}
                  <a
                    href="mailto:dpetrov@kaizenkanri.nl?subject=Kanriva™%20%E2%80%94%20login%20issue&body=Hi%20Daniel%2C%0A%0AI%20can%27t%20sign%20in.%20My%20email%20is%3A%20"
                    className="underline decoration-white/20 underline-offset-2 hover:text-white/60"
                    data-testid="login-help-email"
                  >
                    email us
                  </a>
                  .
                </p>
              </div>
            </div>
          </div>
        </main>

        {/* Quote — the floor, not the office */}
        <footer className="px-6 pb-8 sm:px-10 sm:pb-10">
          <div className="max-w-xl border-l-2 border-cyan-400/50 pl-4 sm:pl-5 animate-[fadeIn_1.1s_ease-out_both]">
            <blockquote className="text-sm font-light leading-relaxed text-white/70 sm:text-base">
              “The secret of change is to focus all of your energy not on fighting the old, but on building the new.”
            </blockquote>
            <p className="mt-2 text-xs uppercase tracking-[0.18em] text-cyan-300/70">— Socrates</p>
          </div>
        </footer>
      </div>
    </div>
  );
};

export const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    company: '',
    industry: 'logistics'
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await register(formData.name, formData.email, formData.password, formData.company, formData.industry);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left - Form */}
      <div className="flex flex-col p-8 lg:p-12">
        <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors w-fit mb-8">
          <ArrowLeft className="h-4 w-4" />
          <span className="text-sm font-medium">Back to home</span>
        </Link>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md space-y-8">
            <div className="text-center">
              <span className="inline-flex items-start justify-center mb-5">
                <img src={LOGO_URL_LIGHT} alt="Kanriva™" className="h-12" />
              </span>
              <h1 className="text-3xl font-bold tracking-tight">Create an account</h1>
              <p className="text-muted-foreground mt-2 font-body">Start your continuous improvement journey</p>
            </div>

            <Card className="border-0 shadow-none">
              <CardContent className="p-0 space-y-6">
                <Button 
                  variant="outline" 
                  className="w-full h-12 gap-3"
                  onClick={handleGoogleLogin}
                  data-testid="google-register-btn"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-background px-2 text-muted-foreground">Or register with email</span>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="name"
                        placeholder="Daniel Petrov"
                        value={formData.name}
                        onChange={(e) => handleChange('name', e.target.value)}
                        className="pl-10 h-12"
                        required
                        data-testid="register-name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@company.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        className="pl-10 h-12"
                        required
                        data-testid="register-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground z-10" />
                      <PasswordInput
                        id="password"
                        placeholder="Min. 8 characters"
                        value={formData.password}
                        onChange={(e) => handleChange('password', e.target.value)}
                        className="pl-10 h-12"
                        required
                        minLength={8}
                        testId="register-password"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company">Company (Optional)</Label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="company"
                        placeholder="Your Company"
                        value={formData.company}
                        onChange={(e) => handleChange('company', e.target.value)}
                        className="pl-10 h-12"
                        data-testid="register-company"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="industry">Industry</Label>
                    <Select value={formData.industry} onValueChange={(v) => handleChange('industry', v)}>
                      <SelectTrigger className="h-12" data-testid="register-industry">
                        <Factory className="h-4 w-4 mr-2 text-muted-foreground" />
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {industries.map((ind) => (
                          <SelectItem key={ind.value} value={ind.value}>{ind.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12" 
                    disabled={loading}
                    data-testid="register-submit"
                  >
                    {loading ? 'Creating account...' : 'Create account'}
                  </Button>
                </form>

                <p className="text-center text-sm text-muted-foreground">
                  Already have an account?{' '}
                  <Link to="/login" className="text-primary font-medium hover:underline">
                    Sign in
                  </Link>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Right - Image */}
      <div className="hidden lg:block relative bg-muted">
        <img 
          src={LOGIN_BG_ALT}
          alt="Continuous Improvement" 
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <blockquote className="text-2xl font-light leading-relaxed">
            "Excellence is not a destination but a continuous journey that never ends."
          </blockquote>
          <p className="mt-4 text-white/80 font-body">— Brian Tracy</p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
